import { type ShopifyRequestOptions, shopifyFetch } from '../client';
import { CART_FRAGMENT } from '../fragments';
import type { Cart } from '../types';

const CREATE_CART_MUTATION = /* GraphQL */ `
  mutation CreateCart($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        ...CartFields
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CART_FRAGMENT}
`;

const ADD_TO_CART_MUTATION = /* GraphQL */ `
  mutation AddToCart($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        ...CartFields
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CART_FRAGMENT}
`;

const UPDATE_CART_LINES_MUTATION = /* GraphQL */ `
  mutation UpdateCartLines($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        ...CartFields
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CART_FRAGMENT}
`;

const REMOVE_FROM_CART_MUTATION = /* GraphQL */ `
  mutation RemoveFromCart($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        ...CartFields
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CART_FRAGMENT}
`;

const UPDATE_CART_BUYER_IDENTITY_MUTATION = /* GraphQL */ `
  mutation UpdateCartBuyerIdentity(
    $cartId: ID!
    $buyerIdentity: CartBuyerIdentityInput!
  ) {
    cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
      cart {
        ...CartFields
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CART_FRAGMENT}
`;

const UPDATE_CART_ATTRIBUTES_MUTATION = /* GraphQL */ `
  mutation UpdateCartAttributes($cartId: ID!, $attributes: [AttributeInput!]!) {
    cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
      cart {
        ...CartFields
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CART_FRAGMENT}
`;

interface CartUserError {
	field: string[];
	message: string;
}

interface CartLineAttributeInput {
	key: string;
	value: string;
}

type CartAttributeInput = CartLineAttributeInput;

interface CartLineInputShape {
	merchandiseId: string;
	quantity: number;
	/** Selling plan (Subscribe & Save) to attach the line to. */
	sellingPlanId?: string;
	attributes?: CartLineAttributeInput[];
}

function handleCartErrors(userErrors: CartUserError[]) {
	if (userErrors.length > 0) {
		const messages = userErrors.map((e) => `${e.field.join('.')}: ${e.message}`).join('\n');
		throw new Error(`Cart error:\n${messages}`);
	}
}

export async function createCart(
	lines: CartLineInputShape[] = [],
	options?: ShopifyRequestOptions & {
		buyerIdentity?: {
			email?: string;
			phone?: string;
			countryCode?: string;
			customerAccessToken?: string;
		};
	},
) {
	const input = options?.buyerIdentity
		? { lines, buyerIdentity: options.buyerIdentity }
		: { lines };
	const data = await (options?.executeGraphql ?? shopifyFetch)<{
		cartCreate: { cart: Cart; userErrors: CartUserError[] };
	}>({
		query: CREATE_CART_MUTATION,
		cartOperation: 'create',
		onCartRequestDiagnostic: options?.onCartRequestDiagnostic,
		variables: { input },
		headers: options?.headers,
		onResponseHeaders: options?.onResponseHeaders,
		cache: 'no-store',
	});

	handleCartErrors(data.cartCreate.userErrors);
	return data.cartCreate.cart;
}

export async function addToCart(
	cartId: string,
	lines: CartLineInputShape[],
	options?: ShopifyRequestOptions,
) {
	const data = await (options?.executeGraphql ?? shopifyFetch)<{
		cartLinesAdd: { cart: Cart; userErrors: CartUserError[] };
	}>({
		query: ADD_TO_CART_MUTATION,
		cartOperation: 'add',
		onCartRequestDiagnostic: options?.onCartRequestDiagnostic,
		variables: { cartId, lines },
		headers: options?.headers,
		onResponseHeaders: options?.onResponseHeaders,
		cache: 'no-store',
	});

	handleCartErrors(data.cartLinesAdd.userErrors);
	return data.cartLinesAdd.cart;
}

export async function updateCartLines(
	cartId: string,
	lines: {
		id: string;
		quantity: number;
		sellingPlanId?: string;
		/** Replaces the line's attributes in full (Shopify CartLineUpdateInput semantics). */
		attributes?: { key: string; value: string }[];
	}[],
	options?: ShopifyRequestOptions,
) {
	const data = await (options?.executeGraphql ?? shopifyFetch)<{
		cartLinesUpdate: { cart: Cart; userErrors: CartUserError[] };
	}>({
		query: UPDATE_CART_LINES_MUTATION,
		cartOperation: 'update-lines',
		onCartRequestDiagnostic: options?.onCartRequestDiagnostic,
		variables: { cartId, lines },
		headers: options?.headers,
		onResponseHeaders: options?.onResponseHeaders,
		cache: 'no-store',
	});

	handleCartErrors(data.cartLinesUpdate.userErrors);
	return data.cartLinesUpdate.cart;
}

export async function removeFromCart(
	cartId: string,
	lineIds: string[],
	options?: ShopifyRequestOptions,
) {
	const data = await (options?.executeGraphql ?? shopifyFetch)<{
		cartLinesRemove: { cart: Cart; userErrors: CartUserError[] };
	}>({
		query: REMOVE_FROM_CART_MUTATION,
		cartOperation: 'remove',
		onCartRequestDiagnostic: options?.onCartRequestDiagnostic,
		variables: { cartId, lineIds },
		headers: options?.headers,
		onResponseHeaders: options?.onResponseHeaders,
		cache: 'no-store',
	});

	handleCartErrors(data.cartLinesRemove.userErrors);
	return data.cartLinesRemove.cart;
}

const UPDATE_CART_DISCOUNT_CODES_MUTATION = /* GraphQL */ `
  mutation UpdateCartDiscountCodes($cartId: ID!, $discountCodes: [String!]!) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        ...CartFields
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CART_FRAGMENT}
`;

export async function updateCartDiscountCodes(
	cartId: string,
	discountCodes: string[],
	options?: ShopifyRequestOptions,
) {
	const data = await (options?.executeGraphql ?? shopifyFetch)<{
		cartDiscountCodesUpdate: { cart: Cart; userErrors: CartUserError[] };
	}>({
		query: UPDATE_CART_DISCOUNT_CODES_MUTATION,
		cartOperation: 'discount-codes',
		onCartRequestDiagnostic: options?.onCartRequestDiagnostic,
		variables: { cartId, discountCodes },
		headers: options?.headers,
		onResponseHeaders: options?.onResponseHeaders,
		cache: 'no-store',
	});

	handleCartErrors(data.cartDiscountCodesUpdate.userErrors);
	return data.cartDiscountCodesUpdate.cart;
}

export async function updateCartBuyerIdentity(
	cartId: string,
	buyerIdentity: {
		email?: string;
		phone?: string;
		countryCode?: string;
		customerAccessToken?: string;
	},
	options?: ShopifyRequestOptions,
) {
	const data = await (options?.executeGraphql ?? shopifyFetch)<{
		cartBuyerIdentityUpdate: { cart: Cart; userErrors: CartUserError[] };
	}>({
		query: UPDATE_CART_BUYER_IDENTITY_MUTATION,
		cartOperation: 'buyer-identity',
		onCartRequestDiagnostic: options?.onCartRequestDiagnostic,
		variables: { cartId, buyerIdentity },
		headers: options?.headers,
		onResponseHeaders: options?.onResponseHeaders,
		cache: 'no-store',
	});

	handleCartErrors(data.cartBuyerIdentityUpdate.userErrors);
	return data.cartBuyerIdentityUpdate.cart;
}

export async function updateCartAttributes(
	cartId: string,
	attributes: CartAttributeInput[],
	options?: ShopifyRequestOptions,
) {
	const data = await (options?.executeGraphql ?? shopifyFetch)<{
		cartAttributesUpdate: { cart: Cart; userErrors: CartUserError[] };
	}>({
		query: UPDATE_CART_ATTRIBUTES_MUTATION,
		cartOperation: 'attributes',
		onCartRequestDiagnostic: options?.onCartRequestDiagnostic,
		variables: { cartId, attributes },
		headers: options?.headers,
		onResponseHeaders: options?.onResponseHeaders,
		cache: 'no-store',
	});

	handleCartErrors(data.cartAttributesUpdate.userErrors);
	return data.cartAttributesUpdate.cart;
}
